const clampScore = (score) => Math.max(1, Math.min(10, score));
const uniq = (items) => Array.from(new Set((items || []).filter(Boolean)));

// helper: tolerate both VI/EN keys or different casing
const get = (obj, ...keys) => {
  for (const key of keys) {
    if (!key) continue;
    if (obj?.[key] != null) return obj[key];
  }
  return 0;
};

export function wealthRule({ tenGodCounts } = {}) {
  const notes = [];
  const recommendations = [];
  const reasoning = [];
  let score = 6;

  const ChinhTai = get(tenGodCounts, 'ChinhTai', 'ChínhTài', 'Chính Tài');
  const ThienTai = get(tenGodCounts, 'ThienTai', 'ThiênTài', 'Thiên Tài');
  const ChinhQuan = get(tenGodCounts, 'ChinhQuan', 'ChínhQuan', 'Chính Quan');
  const ThienQuan = get(tenGodCounts, 'ThienQuan', 'ThiênQuan', 'Thiên Quan');
  const ChinhAn = get(tenGodCounts, 'ChinhAn', 'ChínhẤn', 'Chính Ấn');
  const ThienAn = get(tenGodCounts, 'ThienAn', 'ThiênẤn', 'Thiên Ấn');
  const ThucThan = get(tenGodCounts, 'ThucThan', 'ThựcThần', 'Thực Thần');
  const ThuongQuan = get(tenGodCounts, 'ThuongQuan', 'ThươngQuan', 'Thương Quan');
  const TyKien = get(tenGodCounts, 'TyKien', 'TỷKiên', 'Tỷ Kiên');
  const KiepTai = get(tenGodCounts, 'KiepTai', 'KiếpTài', 'Kiếp Tài');

  // -------------------------
  // 1) Core cashflow stability
  // -------------------------
  if (ChinhTai >= 2 && ThucThan >= 1) {
    score += 2;
    notes.push('Tài vận ổn định, hợp thu nhập bền (Chính Tài + Thực Thần)');
    recommendations.push('Tích lũy đều, ưu tiên danh mục an toàn/ổn định');
    reasoning.push({
      factor: 'Chính Tài ≥ 2 + Thực Thần ≥ 1',
      impact: 2,
      explanation: 'Thực Thần sinh tài: dòng tiền có xu hướng đều và bền.'
    });
  }

  if (ThucThan >= 3 && (ChinhTai + ThienTai) === 0) {
    score -= 1;
    notes.push('Giỏi tạo giá trị nhưng dễ chậm ‘chốt’ thành tiền (Thực Thần mạnh, thiếu Tài)');
    recommendations.push('Rèn kỹ năng chốt giá/định giá sản phẩm, tạo kênh thu nhập rõ');
    reasoning.push({
      factor: 'Thực Thần ≥ 3 + (Chính Tài + Thiên Tài) = 0',
      impact: -1,
      explanation: 'Thiên về sản xuất/nuôi dài hạn nhưng thiếu cơ chế thương mại hóa.'
    });
  }

  // -------------------------
  // 2) Opportunity / volatility
  // -------------------------
  if (ThienTai >= 1 && KiepTai >= 1) {
    score -= 1;
    notes.push('Thiên Tài + Kiếp Tài: tài vận lên xuống, dễ gặp quyết định rủi ro');
    recommendations.push('Tránh ‘đầu tư nóng’, đặt giới hạn rủi ro & đa dạng hóa');
    reasoning.push({
      factor: 'Thiên Tài ≥ 1 + Kiếp Tài ≥ 1',
      impact: -1,
      explanation: 'Thiên Tài thiên cơ hội; Kiếp Tài làm tăng tính cạnh tranh/hao tán.'
    });
  }

  if (ThienTai >= 1 && (ChinhQuan + ThienQuan + ChinhAn + ThienAn) >= 2) {
    score += 1;
    notes.push('Biết nắm cơ hội nhưng có kỷ luật/quy trình (Thiên Tài + Quan/Ấn)');
    recommendations.push('Dùng ‘khung’ quản trị rủi ro: tỷ trọng, điểm dừng, kế hoạch chốt lời');
    reasoning.push({
      factor: 'Thiên Tài ≥ 1 + (Quan + Ấn) ≥ 2',
      impact: 1,
      explanation: 'Quan/Ấn giúp kiểm soát, giảm bốc đồng khi gặp cơ hội.'
    });
  }

  // -------------------------
  // 3) Skill monetization
  // -------------------------
  if (ThuongQuan >= 1 && ChinhTai >= 1) {
    score += 1;
    notes.push('Thương Quan sinh Tài, kiếm tiền từ tài năng');
    recommendations.push('Đầu tư vào kỹ năng lõi, xây sản phẩm/dịch vụ có thể bán');
    reasoning.push({
      factor: 'Thương Quan ≥ 1 + Chính Tài ≥ 1',
      impact: 1,
      explanation: 'Năng lực tạo đầu ra rõ ràng, dễ chuyển hóa thành thu nhập.'
    });
  }

  if (ThuongQuan >= 3 && (ChinhQuan + ThienQuan + ChinhAn + ThienAn) === 0) {
    score -= 1;
    notes.push('Tài năng mạnh nhưng dễ thiếu kỷ luật/hệ thống (Thương Quan mạnh, thiếu Quan/Ấn)');
    recommendations.push('Thiết lập quy trình, KPI cá nhân, lịch làm việc/chi tiêu cố định');
    reasoning.push({
      factor: 'Thương Quan ≥ 3 + (Quan + Ấn) = 0',
      impact: -1,
      explanation: 'Thiếu ‘khung’ kiểm soát khiến dòng tiền dễ thất thường.'
    });
  }

  // -------------------------
  // 4) Leakage / competition
  // -------------------------
  if (TyKien >= 3 && ChinhTai >= 1) {
    score -= 1;
    notes.push('Tỷ Kiên đoạt Tài, dễ hao hụt vì bạn bè/đối tác');
    recommendations.push('Quản lý chi tiêu, hạn chế cho vay');
    reasoning.push({
      factor: 'Tỷ Kiên ≥ 3 + Chính Tài ≥ 1',
      impact: -1,
      explanation: 'Cạnh tranh nội tại dễ làm tài chính bị phân tán.'
    });
  }

  if (KiepTai >= 2 && (ChinhTai + ThienTai) >= 1) {
    score -= 1;
    notes.push('Kiếp Tài mạnh: cần ranh giới rõ trong hợp tác/đồng tiền');
    recommendations.push('Tách bạch tài khoản cá nhân–công việc; hợp tác phải có điều khoản rõ');
    reasoning.push({
      factor: 'Kiếp Tài ≥ 2 + (Chính Tài + Thiên Tài) ≥ 1',
      impact: -1,
      explanation: 'Tính cạnh tranh/chiếm dụng nguồn lực khiến rủi ro hao tán tăng.'
    });
  }

  if (TyKien >= 1 && TyKien <= 2 && ThucThan >= 1 && ChinhTai >= 1) {
    score += 1;
    notes.push('Tự lực tạo dòng tiền tốt (Tỷ Kiên vừa + Thực Thần + Tài)');
    recommendations.push(
      'Giữ kỷ luật tiết kiệm/đầu tư định kỳ; ưu tiên tăng thu nhập chủ động'
    );
    reasoning.push({
      factor: 'Tỷ Kiên 1–2 + Thực Thần ≥ 1 + Chính Tài ≥ 1',
      impact: 1,
      explanation: 'Tự lực + tạo giá trị giúp tích lũy bền hơn.'
    });
  }

  // -------------------------
  // 5) Governance / risk control (Quan)
  // -------------------------
  if ((ChinhQuan + ThienQuan) >= 2 && (ChinhTai + ThienTai) >= 1) {
    score += 1;
    notes.push('Có xu hướng quản trị tài chính tốt nhờ kỷ luật (Quan + Tài)');
    recommendations.push(
      'Duy trì ngân sách, rebalancing theo kỳ; tránh phá kỷ luật vì biến động ngắn hạn'
    );
    reasoning.push({
      factor: '(Chính Quan + Thiên Quan) ≥ 2 + (Tài) ≥ 1',
      impact: 1,
      explanation: 'Quan tinh thường hỗ trợ nguyên tắc và kiểm soát rủi ro.'
    });
  }

  if ((ChinhQuan + ThienQuan) >= 3 && (ThucThan + ThuongQuan) === 0) {
    score -= 1;
    notes.push(
      'Thiên an toàn/khuôn khổ, tăng trưởng tài chính có thể chậm (Quan nhiều, thiếu Thực/Thương)'
    );
    recommendations.push(
      'Bổ sung ‘động cơ tăng trưởng’: kỹ năng mới, dự án phụ, mở rộng nguồn thu'
    );
    reasoning.push({
      factor: '(Quan) ≥ 3 + (Thực Thần + Thương Quan) = 0',
      impact: -1,
      explanation: 'Quá thiên kiểm soát, thiếu ‘đầu ra’ tạo tăng trưởng.'
    });
  }

  // -------------------------
  // 6) Support / learning (Ấn)
  // -------------------------
  if ((ChinhAn + ThienAn) >= 2 && (ChinhTai + ThienTai) >= 1) {
    score += 1;
    notes.push('Có nền tảng học hỏi và tích lũy kiến thức tài chính (Ấn + Tài)');
    recommendations.push('Ưu tiên chiến lược hiểu rõ: dài hạn, minh bạch, kiểm chứng được');
    reasoning.push({
      factor: '(Chính Ấn + Thiên Ấn) ≥ 2 + (Tài) ≥ 1',
      impact: 1,
      explanation: 'Ấn tinh hỗ trợ học – hiểu – làm bài bản, giảm sai lầm cảm tính.'
    });
  }

  if ((ChinhAn + ThienAn) >= 3 && (ChinhTai + ThienTai) === 0) {
    score -= 1;
    notes.push('Học nhiều nhưng dễ chậm chuyển hóa thành thu nhập (Ấn nhiều, thiếu Tài)');
    recommendations.push(
      'Gắn việc học với sản phẩm/dịch vụ cụ thể; đặt KPI ‘đầu ra’'
    );
    reasoning.push({
      factor: '(Ấn) ≥ 3 + (Tài) = 0',
      impact: -1,
      explanation: 'Thiên về tích lũy tri thức, thiếu cơ chế kiếm tiền trực tiếp.'
    });
  }

  // -------------------------
  // 7) Edge cases: extremes
  // -------------------------
  if ((ChinhTai + ThienTai) >= 3 && (ThucThan + ThuongQuan) === 0) {
    score -= 1;
    notes.push('Tài tinh mạnh nhưng thiếu ‘đầu ra’ → dễ phụ thuộc nguồn thu sẵn có');
    recommendations.push('Bổ sung năng lực tạo giá trị: kỹ năng, sản phẩm, hoặc kênh tăng thu');
    reasoning.push({
      factor: '(Tài) ≥ 3 + (Thực Thần + Thương Quan) = 0',
      impact: -1,
      explanation: 'Thiếu ‘sinh tài’ làm tốc độ tăng trưởng tài sản chậm.'
    });
  }

  if (
    (ThucThan + ThuongQuan) >= 1
    && (ChinhTai + ThienTai) >= 1
    && (ChinhQuan + ThienQuan + ChinhAn + ThienAn) >= 1
  ) {
    score += 1;
    notes.push('Cấu trúc tài chính tương đối cân bằng: tạo giá trị + kiếm tiền + kiểm soát');
    recommendations.push('Giữ kỷ luật phân bổ: core ổn định + phần tăng trưởng vừa phải');
    reasoning.push({
      factor: '(Thực/Thương) ≥ 1 + (Tài) ≥ 1 + (Quan/Ấn) ≥ 1',
      impact: 1,
      explanation: 'Có đầu ra tạo giá trị, có kênh kiếm tiền, và có cơ chế kiểm soát.'
    });
  }

  if (!notes.length) {
    notes.push('Tài vận trung bình, trọng tâm là quản lý thu–chi và xây kỹ năng tạo thu nhập');
    recommendations.push(
      'Lập ngân sách, quỹ dự phòng; ưu tiên tích lũy đều và học quản trị rủi ro'
    );
    reasoning.push({
      factor: 'No strong pattern',
      impact: 0,
      explanation: 'Chưa thấy tổ hợp nổi bật; nên ưu tiên kỷ luật tài chính cơ bản.'
    });
  }

  return {
    rating: clampScore(score),
    nhanXet: notes.join('; '),
    khuyenNghi: uniq(recommendations),
    reasoning
  };
}
